import { z } from 'zod'

// =====================
// Ticket Macros (canned responses)
// =====================
//
// A macro is a saved reply insertable into the ticket reply composer.
// ORG scope is shared with the whole org (managed by admins); PERSONAL
// scope is private to its owner. bodyHtml supports {{variable}}
// placeholders resolved at insert time, and `actions` optionally applies
// ticket mutations when the macro is used.

export enum TicketMacroScopeEnum {
  ORG = 'ORG',
  PERSONAL = 'PERSONAL',
}

// Variables the composer substitutes at insert time. Keep in sync with the
// resolver on both API (apply endpoint) and web (client-side preview).
export const TICKET_MACRO_VARIABLES = [
  { key: 'requester.name', label: 'Requester name' },
  { key: 'requester.firstName', label: 'Requester first name' },
  { key: 'requester.email', label: 'Requester email' },
  { key: 'ticket.number', label: 'Ticket number' },
  { key: 'ticket.subject', label: 'Ticket subject' },
  { key: 'agent.name', label: 'Your name' },
  { key: 'organization.name', label: 'Organization name' },
] as const

export type TicketMacroVariableKey = (typeof TICKET_MACRO_VARIABLES)[number]['key']

// Optional side effects applied to the ticket when a macro is used.
export const TicketMacroActionsSchema = z.object({
  setStage: z.string().max(50).optional(),
  setPriority: z.string().max(20).optional(),
  addTags: z.array(z.string().max(50)).max(20).optional(),
  assignUserIds: z.array(z.string().uuid()).max(20).optional(),
  addFollowerIds: z.array(z.string().uuid()).max(20).optional(),
})

export type TicketMacroActions = z.infer<typeof TicketMacroActionsSchema>

export const TicketMacroSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string().min(1).max(120),
  description: z.string().nullable().optional(),
  scope: z.nativeEnum(TicketMacroScopeEnum).default(TicketMacroScopeEnum.ORG),
  // Set only for PERSONAL macros.
  ownerId: z.string().uuid().nullable().optional(),
  bodyHtml: z.string().default(''),
  bodyText: z.string().nullable().optional(),
  actions: TicketMacroActionsSchema.default({}),
  isEnabled: z.boolean().default(true),
  order: z.number().int().default(0),
  usageCount: z.number().int().default(0),
  lastUsedAt: z.coerce.date().nullable().optional(),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type TicketMacro = z.infer<typeof TicketMacroSchema>

export const CreateTicketMacroSchema = TicketMacroSchema.omit({
  id: true,
  organizationId: true,
  ownerId: true,
  usageCount: true,
  lastUsedAt: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
})

export type CreateTicketMacro = z.infer<typeof CreateTicketMacroSchema>

export const UpdateTicketMacroSchema = CreateTicketMacroSchema.partial()

export type UpdateTicketMacro = z.infer<typeof UpdateTicketMacroSchema>
